import React, { useEffect, useState, useMemo } from "react";
import API_URL from "../config";
import {
  FaSpinner,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaDownload,
  FaUndo,
  FaTrash,
  FaArchive,
  FaCheckCircle,
} from "react-icons/fa";
import * as XLSX from "xlsx";
import "./SalesList.css";

// Capacitor imports
import { Filesystem, Directory } from "@capacitor/filesystem";
import { Share } from "@capacitor/share";
import { Capacitor } from "@capacitor/core";

export default function SalesList({
  entrepriseId,
  siteId,
  userType,
  type_user,
  typeUser,
}) {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- Filtres ---
  const [filterProduit, setFilterProduit] = useState("");
  const [filterClient, setFilterClient] = useState("");
  const [filterVendeur, setFilterVendeur] = useState("");
  const [filterDenomination, setFilterDenomination] = useState("");
  const [filterDateStart, setFilterDateStart] = useState("");
  const [filterDateEnd, setFilterDateEnd] = useState("");

  const [showArchiveModal, setShowArchiveModal] = useState(false);

  // --- Tri ---
  const [sortField, setSortField] = useState("date_vente");
  const [sortOrder, setSortOrder] = useState("desc");

  // --- Listes dynamiques ---
  const [availableProduits, setAvailableProduits] = useState([]);
  const [availableClients, setAvailableClients] = useState([]);
  const [availableVendeurs, setAvailableVendeurs] = useState([]);
  const [availableDenominations, setAvailableDenominations] = useState([]);

  // --- Modal suppression gestionnaire ---
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");
  const [selectedSaleId, setSelectedSaleId] = useState(null);

  // --- Fonctions utilitaires ---
  const normalizeText = (value) => {
    return String(value ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ");
  };

  const normalizeDenomination = (value) => {
    return normalizeText(String(value ?? "").replace(/\(.*?\)/g, "").trim());
  };

  const getEndOfDay = (dateString) => {
    if (!dateString) return null;
    const d = new Date(dateString);
    d.setHours(23, 59, 59, 999);
    return d;
  };

  const formatDateTimeForExcel = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleString("fr-FR");
  };

  const formatDateForTable = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toLocaleDateString("fr-FR");
  };

  const safeNumber = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  };

  const autoFitColumns = (rows, minimumWidths = {}) => {
    if (!rows || rows.length === 0) return [];

    const keys = Object.keys(rows[0]);

    return keys.map((key) => {
      const headerLength = String(key).length;
      const maxDataLength = rows.reduce((max, row) => {
        const cellValue = row[key] ?? "";
        return Math.max(max, String(cellValue).length);
      }, 0);

      const computedWidth = Math.max(headerLength, maxDataLength) + 4;
      const minWidth = minimumWidths[key] || 12;
      const finalWidth = Math.min(Math.max(computedWidth, minWidth), 45);

      return { wch: finalWidth };
    });
  };

  // --- Détection du type d'utilisateur ---
  const isGestionnaire = useMemo(() => {
    let t = userType ?? type_user ?? typeUser ?? "";
    if (!t && typeof window !== "undefined") {
      const keys = ["type_user", "userType", "typeUser", "type", "role", "user"];
      for (let key of keys) {
        const val = localStorage.getItem(key);
        if (!val) continue;
        try {
          const parsed = JSON.parse(val);
          t =
            parsed?.type_user ||
            parsed?.type ||
            parsed?.role ||
            parsed?.typeUser ||
            val;
          break;
        } catch {
          t = val;
          break;
        }
      }
    }
    return String(t || "").toLowerCase().includes("gestion");
  }, [userType, type_user, typeUser]);

  // ----------- FETCH VENTES -----------
  useEffect(() => {
    if (!entrepriseId || !siteId) return;

    const fetchSales = async () => {
      setLoading(true);
      setError(null);

      try {
        const res = await fetch(`${API_URL}/api/sales/${entrepriseId}/${siteId}`);
        if (!res.ok) throw new Error("Erreur lors du chargement des ventes");

        const data = await res.json();

        const enriched = data.map((sale) => {
          const montantTotal =
            safeNumber(sale.montant) ||
            safeNumber(sale.prix_unitaire) * safeNumber(sale.quantite) ||
            0;

          const montantPaye = safeNumber(sale.montant_payer);

          let statutPaiement = "Inconnu";
          if (montantTotal > 0) {
            if (montantPaye >= montantTotal) {
              statutPaiement = "Payé (Cash)";
            } else if (montantPaye > 0 && montantPaye < montantTotal) {
              statutPaiement = `Partiel (${(montantTotal - montantPaye).toFixed(
                2
              )} F de dette)`;
            } else if (sale.client_id) {
              statutPaiement = "Crédit (Dette totale)";
            } else {
              statutPaiement = "Non Payé / Anonyme";
            }
          }

          return {
            ...sale,
            produit: sale.produit || "Inconnu",
            client: sale.client || "Inconnu",
            vendeur: sale.vendeur || "Inconnu",
            denomination: sale.lots_utilises || "RAS",
            prix_unitaire: safeNumber(sale.prix_unitaire),
            quantite: safeNumber(sale.quantite),
            montant: montantTotal,
            montant_payer: montantPaye,
            statut_paiement: statutPaiement,
          };
        });

        setSales(enriched);

        const extractUnique = (fn) =>
          Array.from(new Set(enriched.map(fn).filter(Boolean))).sort((a, b) =>
            String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
          );

        setAvailableProduits(extractUnique((s) => s.produit));
        setAvailableClients(extractUnique((s) => s.client));
        setAvailableVendeurs(extractUnique((s) => s.vendeur));

        const allLots = enriched
          .flatMap((s) =>
            String(s.denomination || "")
              .split(",")
              .map((lot) => lot.replace(/\(.*?\)/g, "").trim())
          )
          .filter(Boolean);

        setAvailableDenominations(
          Array.from(new Set(allLots)).sort((a, b) =>
            String(a).localeCompare(String(b), "fr", { sensitivity: "base" })
          )
        );
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchSales();
  }, [entrepriseId, siteId]);

  const handleDelete = (saleId) => {
    if (isGestionnaire) {
      setSelectedSaleId(saleId);
      setDeleteReason("");
      setShowDeleteModal(true);
    }
  };

  const deleteSale = async (saleId) => {
    try {
      const res = await fetch(
        `${API_URL}/api/sales/${saleId}/${entrepriseId}/${siteId}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ motif: deleteReason }),
        }
      );

      if (!res.ok) throw new Error("Erreur lors de la suppression");

      setSales((prev) => prev.filter((s) => s.id !== saleId));

      setShowDeleteModal(false);
      setShowSuccessModal(true);

      setDeleteReason("");
      setSelectedSaleId(null);
    } catch (err) {
      alert("❌ Impossible de supprimer la vente : " + err.message);
    }
  };

  const confirmDelete = () => {
    if (!deleteReason.trim()) {
      alert("Veuillez saisir un motif.");
      return;
    }
    deleteSale(selectedSaleId);
  };

  const filteredSales = useMemo(() => {
    return sales.filter((s) => {
      const saleDate = s.date_vente ? new Date(s.date_vente) : null;
      const startDate = filterDateStart ? new Date(filterDateStart) : null;
      const endDate = getEndOfDay(filterDateEnd);

      const produitMatch = filterProduit
        ? normalizeText(s.produit) === normalizeText(filterProduit)
        : true;

      const clientMatch = filterClient
        ? normalizeText(s.client) === normalizeText(filterClient)
        : true;

      const vendeurMatch = filterVendeur
        ? normalizeText(s.vendeur) === normalizeText(filterVendeur)
        : true;

      const denominationMatch = filterDenomination
        ? normalizeDenomination(s.denomination) ===
          normalizeDenomination(filterDenomination)
        : true;

      const dateStartMatch = startDate && saleDate ? saleDate >= startDate : true;
      const dateEndMatch = endDate && saleDate ? saleDate <= endDate : true;

      return (
        produitMatch &&
        clientMatch &&
        vendeurMatch &&
        denominationMatch &&
        dateStartMatch &&
        dateEndMatch
      );
    });
  }, [
    sales,
    filterProduit,
    filterClient,
    filterVendeur,
    filterDenomination,
    filterDateStart,
    filterDateEnd,
  ]);

  const showArchiveButton = useMemo(() => {
    const someFilter =
      filterProduit ||
      filterClient ||
      filterVendeur ||
      filterDenomination ||
      filterDateStart ||
      filterDateEnd;

    return Boolean(someFilter && filteredSales.length > 0);
  }, [
    filterProduit,
    filterClient,
    filterVendeur,
    filterDenomination,
    filterDateStart,
    filterDateEnd,
    filteredSales.length,
  ]);

  const handleArchive = () => setShowArchiveModal(true);

  const confirmArchive = async () => {
    try {
      const venteIds = filteredSales.map((s) => s.id);

      const res = await fetch(`${API_URL}/api/archive-ventes/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ venteIds }),
      });

      if (!res.ok) throw new Error("Erreur lors de l’archivage");

      setSales((prev) => prev.filter((s) => !venteIds.includes(s.id)));
      alert("Archivage terminé !");
      setShowArchiveModal(false);
    } catch (err) {
      alert("Erreur : " + err.message);
    }
  };

  const sortedSales = useMemo(() => {
    return [...filteredSales].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === "date_vente") {
        aVal = new Date(aVal);
        bVal = new Date(bVal);
      } else if (
        ["prix_unitaire", "quantite", "montant", "montant_payer"].includes(sortField)
      ) {
        aVal = safeNumber(aVal);
        bVal = safeNumber(bVal);
      } else {
        aVal = normalizeText(aVal);
        bVal = normalizeText(bVal);
      }

      if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
      if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredSales, sortField, sortOrder]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder(field === "date_vente" ? "desc" : "asc");
    }
  };

  const arrayBufferToBase64 = (buffer) => {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  };

  const exportToExcel = async () => {
    try {
      if (filteredSales.length === 0) {
        alert("Aucune vente à exporter.");
        return;
      }

      const exportSales = [...filteredSales].sort((a, b) => {
        const dateA = new Date(a.date_vente);
        const dateB = new Date(b.date_vente);
        return dateB - dateA;
      });

      const rows = exportSales.map((s) => ({
        Date: formatDateTimeForExcel(s.date_vente),
        Produit: s.produit,
        Dénomination: s.denomination,
        "Prix unitaire (FCFA)": safeNumber(s.prix_unitaire),
        Quantité: safeNumber(s.quantite),
        "Montant total (FCFA)": safeNumber(s.montant),
        "Montant payé (FCFA)": safeNumber(s.montant_payer),
        "Statut paiement": s.statut_paiement,
        Client: s.client,
        Vendeur: s.vendeur,
      }));

      const ws = XLSX.utils.json_to_sheet(rows);

      ws["!cols"] = autoFitColumns(rows, {
        Date: 22,
        Produit: 18,
        Dénomination: 22,
        "Prix unitaire (FCFA)": 18,
        Quantité: 12,
        "Montant total (FCFA)": 18,
        "Montant payé (FCFA)": 18,
        "Statut paiement": 28,
        Client: 20,
        Vendeur: 20,
      });

      ws["!rows"] = [
        { hpt: 24 },
        ...rows.map(() => ({ hpt: 22 })),
      ];

      ws["!autofilter"] = {
        ref: XLSX.utils.encode_range({
          s: { c: 0, r: 0 },
          e: { c: Object.keys(rows[0]).length - 1, r: rows.length },
        }),
      };

      ws["!freeze"] = { xSplit: 0, ySplit: 1 };

      const moneyColumns = ["D", "F", "G"];
      const quantityColumn = "E";

      for (let rowIndex = 2; rowIndex <= rows.length + 1; rowIndex++) {
        for (const col of moneyColumns) {
          const cellRef = `${col}${rowIndex}`;
          if (ws[cellRef]) {
            ws[cellRef].z = '#,##0.00';
          }
        }

        const quantityRef = `${quantityColumn}${rowIndex}`;
        if (ws[quantityRef]) {
          ws[quantityRef].z = "#,##0";
        }
      }

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Ventes");

      const wbout = XLSX.write(wb, {
        bookType: "xlsx",
        type: "array",
        cellStyles: true,
      });

      const filename = `ventes_${new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-")}.xlsx`;

      const platform = Capacitor.getPlatform();

      if (platform === "web") {
        const blob = new Blob([wbout], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else {
        const base64 = arrayBufferToBase64(wbout);
        await Filesystem.writeFile({
          path: filename,
          data: base64,
          directory: Directory.Cache,
        });

        const uriResult = await Filesystem.getUri({
          directory: Directory.Cache,
          path: filename,
        });

        await Share.share({
          title: "Export Ventes",
          text: "Voici l'export des ventes",
          url: uriResult.uri,
        });
      }
    } catch (err) {
      alert("Erreur export : " + err.message);
    }
  };

  const resetFilters = () => {
    setFilterProduit("");
    setFilterClient("");
    setFilterVendeur("");
    setFilterDenomination("");
    setFilterDateStart("");
    setFilterDateEnd("");
  };

  const totalQuantity = filteredSales.reduce(
    (acc, s) => acc + safeNumber(s.quantite),
    0
  );

  const totalAmount = filteredSales.reduce(
    (acc, s) => acc + safeNumber(s.montant),
    0
  );

  if (loading) {
    return (
      <div className="loading">
        <FaSpinner className="spin" /> Chargement...
      </div>
    );
  }

  if (error) {
    return <div className="error">Erreur: {error}</div>;
  }

  return (
    <div className="sales-list-container">
      <h2>Liste des ventes</h2>

      <div className="filters-wrapper">
        <div className="filters">
          <label>
            Du :{" "}
            <input
              type="date"
              value={filterDateStart}
              onChange={(e) => setFilterDateStart(e.target.value)}
            />
          </label>

          <label>
            Au :{" "}
            <input
              type="date"
              value={filterDateEnd}
              onChange={(e) => setFilterDateEnd(e.target.value)}
            />
          </label>

          <select
            value={filterProduit}
            onChange={(e) => setFilterProduit(e.target.value)}
          >
            <option value="">Produit</option>
            {availableProduits.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>

          <select
            value={filterDenomination}
            onChange={(e) => setFilterDenomination(e.target.value)}
          >
            <option value="">Dénomination</option>
            {availableDenominations.map((d) => (
              <option key={d} value={d}>
                {d}
              </option>
            ))}
          </select>

          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
          >
            <option value="">Client</option>
            {availableClients.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <select
            value={filterVendeur}
            onChange={(e) => setFilterVendeur(e.target.value)}
          >
            <option value="">Vendeur</option>
            {availableVendeurs.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>

          <button className="reset-btn" onClick={resetFilters}>
            <FaUndo /> Réinitialiser
          </button>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          margin: "10px 0",
          gap: "10px",
        }}
      >
        {showArchiveButton && (
          <button
            onClick={handleArchive}
            className="archive-btn"
            style={{
              backgroundColor: "#6c757d",
              color: "#fff",
              border: "none",
              padding: "10px 14px",
              borderRadius: 6,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FaArchive /> Archiver
          </button>
        )}

        <button
          onClick={exportToExcel}
          className="export-btn"
          style={{
            backgroundColor: "#28a745",
            color: "#fff",
            border: "none",
            padding: "10px 14px",
            borderRadius: 6,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <FaDownload /> Exporter Excel
        </button>
      </div>

      <div className="table-wrapper">
        <table className="sales-table">
          <thead>
            <tr>
              {[
                ["date_vente", "Date"],
                ["produit", "Produit"],
                ["denomination", "Dénomination"],
                ["prix_unitaire", "Prix Unitaire"],
                ["quantite", "Quantité"],
                ["montant", "Montant Total"],
                ["montant_payer", "Montant Payé"],
                ["statut_paiement", "Statut Paiement"],
                ["client", "Client"],
                ["vendeur", "Vendeur"],
              ].map(([field, label]) => (
                <th key={field} onClick={() => handleSort(field)}>
                  {label}{" "}
                  {sortField === field ? (
                    sortOrder === "asc" ? (
                      <FaSortUp />
                    ) : (
                      <FaSortDown />
                    )
                  ) : (
                    <FaSort />
                  )}
                </th>
              ))}
              {isGestionnaire && <th>Action</th>}
            </tr>

            <tr style={{ backgroundColor: "#f0f0f0", fontWeight: "bold" }}>
              <td colSpan={4} style={{ textAlign: "right" }}>
                Totaux :
              </td>
              <td>{totalQuantity}</td>
              <td>{totalAmount.toLocaleString("fr-FR")} FCFA</td>
              <td colSpan={isGestionnaire ? 5 : 4}></td>
            </tr>
          </thead>

          <tbody>
            {sortedSales.length === 0 ? (
              <tr>
                <td colSpan={isGestionnaire ? 11 : 10}>
                  Aucune vente trouvée.
                </td>
              </tr>
            ) : (
              sortedSales.map((s) => (
                <tr key={s.id}>
                  <td>{formatDateForTable(s.date_vente)}</td>
                  <td>{s.produit}</td>
                  <td>{s.denomination}</td>
                  <td>{safeNumber(s.prix_unitaire).toFixed(2)}</td>
                  <td>{s.quantite}</td>
                  <td>{safeNumber(s.montant).toFixed(2)}</td>
                  <td>{safeNumber(s.montant_payer).toFixed(2)}</td>
                  <td>{s.statut_paiement}</td>
                  <td>{s.client}</td>
                  <td>{s.vendeur}</td>

                  {isGestionnaire && (
                    <td>
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(s.id)}
                      >
                        <FaTrash />
                      </button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showArchiveModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Archiver ces données ?</h3>
            <p>
              Voulez-vous vraiment <strong>archiver</strong> toutes les ventes
              filtrées ?
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setShowArchiveModal(false)}
                className="cancel-btn"
              >
                Annuler
              </button>
              <button onClick={confirmArchive} className="confirm-btn">
                Oui, archiver
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Motif de suppression</h3>
            <textarea
              placeholder="Expliquez la raison..."
              value={deleteReason}
              onChange={(e) => setDeleteReason(e.target.value)}
              rows={4}
            />
            <div className="modal-actions">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="cancel-btn"
              >
                Annuler
              </button>
              <button onClick={confirmDelete} className="confirm-btn">
                Continuer la suppression
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ textAlign: "center", padding: "30px" }}>
            <FaCheckCircle
              size={50}
              color="#28a745"
              style={{ marginBottom: "15px" }}
            />
            <h3 style={{ color: "#28a745" }}>Succès !</h3>
            <p>
              La vente a été supprimée avec succès et les stocks ont été mis à
              jour.
            </p>
            <div
              className="modal-actions"
              style={{ justifyContent: "center", marginTop: "20px" }}
            >
              <button
                onClick={() => setShowSuccessModal(false)}
                className="confirm-btn"
                style={{ backgroundColor: "#28a745", minWidth: "100px" }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}